import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { FadeInUp } from '../components/animations';
import { getCategoryBySlug } from '../data/categoryData';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import { breadcrumbSchema, serviceSchema } from '../utils/schema';

const CategoryDetailPage = () => {
  const { slug } = useParams();
  const category = getCategoryBySlug(slug);

  if (!category) return <Navigate to="/categories" replace />;

  const process = [
    { step: '01', title: 'Requirement', desc: 'Share your product specifications and requirements' },
    { step: '02', title: 'Manufacturer Identification', desc: 'We identify suitable Indian manufacturers' },
    { step: '03', title: 'Quotation & Comparison', desc: 'Receive and compare competitive quotes' },
    { step: '04', title: 'Sample / Approval', desc: 'Sample development and buyer approval' },
    { step: '05', title: 'Production Coordination', desc: 'End-to-end production follow-up' },
    { step: '06', title: 'Quality Check', desc: 'Quality inspection at key stages' },
    { step: '07', title: 'Packing & Shipment', desc: 'Packaging and shipment coordination' },
  ];

  return (
    <div className="pt-20" data-testid="category-detail-page">
      <SEO
        title={category.seo?.title || `${category.title} Sourcing from India | Taiga Sourcing`}
        description={category.seo?.description || category.heroDescription}
        keywords={category.seo?.keywords || ''}
        path={`/categories/${slug}`}
        schema={[
          serviceSchema(category, `/categories/${slug}`),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Categories', path: '/categories' },
            { name: category.title, path: `/categories/${slug}` },
          ]),
        ]}
      />
      {/* Hero */}
      <PageHero image={category.image}>
        <Link to="/categories" className="inline-flex items-center gap-1 text-xs tracking-wider uppercase text-white/50 hover:text-white/80 transition-colors mb-6">
          &larr; All Categories
        </Link>
        <h1
          className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1] mb-6"
          style={{ fontFamily: 'Cabinet Grotesk' }}
          data-testid="category-title"
        >
          {category.title.toUpperCase()}
        </h1>
        <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
          {category.heroDescription}
        </p>
      </PageHero>

      {/* Overview + Image */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInUp>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A57] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Category Overview
              </h2>
              <p className="text-base text-[#6B7280] leading-relaxed mb-8">
                {category.overview}
              </p>
              <Link
                to="/request-sourcing"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0B2A57] text-white text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-lg hover:-translate-y-0.5"
              >
                Request Sourcing <ArrowRight size={18} />
              </Link>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <img
                src={category.image}
                alt={category.title}
                className="w-full h-[400px] object-cover"
                loading="lazy"
              />
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Subcategories */}
      <section className="py-24 lg:py-32 bg-[#fafaf9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeInUp className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A57] mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Product Subcategories
            </h2>
          </FadeInUp>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {category.subcategories.map((sub, i) => (
              <FadeInUp key={sub} delay={i * 0.05}>
                <div className="p-5 bg-white border border-[#E5E0D8]/60 text-center">
                  <span className="text-sm font-semibold text-[#0B2A57]">{sub}</span>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* What Buyers Request */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <FadeInUp>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A57] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
                What Buyers Can Request
              </h2>
              <div className="space-y-4">
                {category.whatBuyersRequest.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[#1E3D66] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#6B7280] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A57] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Sourcing Process
              </h2>
              <div className="space-y-4">
                {process.map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <span className="text-lg font-bold text-[#0B2A57]/20 flex-shrink-0 w-8" style={{ fontFamily: 'Cabinet Grotesk' }}>{item.step}</span>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0B2A57]">{item.title}</h4>
                      <p className="text-xs text-[#6B7280]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#071E45] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              READY TO SOURCE {category.title.toUpperCase()}?
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-8">
              Share your requirements and we'll help identify suitable Indian manufacturers.
            </p>
            <Link
              to="/request-sourcing"
              className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#0B2A57] text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-xl hover:-translate-y-0.5"
            >
              Request Sourcing <ArrowRight size={18} />
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default CategoryDetailPage;
